import React, { useEffect } from 'react'
import { CheckCircle } from 'lucide-react'

import { IUser, UserCard, UserStore } from '@/entities/user'
import { cn, DEFAULT_PREC } from '@/shared/lib'
import { LangStore } from '@/shared/model/store'
import { Button, Dialog, DialogContent, DialogTitle, DialogTrigger } from '@/shared/ui'
import { AmountBlock } from '@/shared/ui/amount-block/amount-block'
import useRequest from '@/shared/lib/use-request'

type Props = {
	reg?: (npo_address: `0x${string}`) => void
	getFullUser?: (user: IUser) => void
	isLoading?: boolean
	button?: string
	children?: React.ReactNode
	disabled?: `0x${string}`[]
	closeAfterClick?: boolean
	staked?: number | string | bigint
	disabledButton?: boolean
}

const NpoModal = ({
	reg,
	getFullUser,
	isLoading,
	button,
	children,
	disabled,
	closeAfterClick,
	staked,
	disabledButton
}: Props) => {
	const { langPack } = LangStore()
	const [isOpen, setIsOpen] = React.useState(false)
	const [selectedNpo, setSelectedNpo] = React.useState<IUser>({} as IUser)
	const { user_npos, setUserNpos, userData } = UserStore()
	const { fetch } = useRequest<IUser[]>('listNpo')

	useEffect(() => {
		if (!isOpen) return
		fetch({
			params: {
				user_addr: userData?.address,
				limit: 100,
				confirmed_only: true
			},
			onSuccess: async res => {
				setUserNpos(res)
			}
		})
	}, [isOpen])

	if (!user_npos || user_npos?.length === 0) return null

	const filteredNpos = user_npos?.filter((el: IUser) => !disabled?.includes(el.address))

	const handleNpoSelect = (npo: IUser) => {
		setSelectedNpo(npo)
	}

	const handleSubmit = () => {
		reg && reg(selectedNpo?.address)
		getFullUser && getFullUser(selectedNpo)
		closeAfterClick && setIsOpen(false)
	}

	return (
		<Dialog open={isOpen} onOpenChange={setIsOpen}>
			<DialogTrigger asChild>
				{children || <Button>{`${langPack('action.create')} ${langPack('npo.onBehalfOfNpo')}`}</Button>}
			</DialogTrigger>

			<DialogContent>
				<DialogTitle>{langPack('npo.selectNpo')}</DialogTitle>

				<div className='flex flex-col gap-4'>
					{filteredNpos.map((npo: IUser) => {
						const isDisabled = (!!staked && Number(staked) * DEFAULT_PREC > Number(npo.staked)) || disabledButton
						const isSelected = npo.address === selectedNpo?.address

						return (
							<div
								className={cn('flex w-full items-center justify-between', isDisabled ? 'opacity-50' : '')}
								key={npo.address}
							>
								<UserCard user={npo} onClick={e => e.preventDefault()} />

								<div className='flex flex-row items-center gap-2'>
									{!!staked && <AmountBlock withoutIcon amount={npo.staked} token='SAVVA' />}

									<Button
										onClick={() => handleNpoSelect(npo)}
										size={'sm'}
										variant={isSelected ? 'success' : 'outline'}
										disabled={isDisabled}
									>
										<CheckCircle size={12} />
									</Button>
								</div>
							</div>
						)
					})}
				</div>

				<Button disabled={!selectedNpo?.address} onClick={handleSubmit} loading={isLoading}>
					{button || 'Опубликовать'}
				</Button>
			</DialogContent>
		</Dialog>
	)
}

export default NpoModal
